
import { GeneratedBook, BookPage } from '../types';
import { getSupabase } from './supabaseClient';
import { debugLog } from '../utils/debugLogger';

const TABLE_NAME = 'stories';
const BUCKET_NAME = 'library-assets';
const IMAGE_ERRORS_TABLE = 'image_errors';
const LIMIT_FREE = 2;
const LIMIT_PRO = 10;

/**
 * Logs image load errors to Supabase for diagnostics.
 * Only logs actual errors - success events are skipped.
 * Base64 data URIs are not stored - only a placeholder is logged.
 */
export async function logImageEvent(
  eventType: 'load_success' | 'load_error' | 'upload_error',
  imageUrl: string,
  bookId?: string,
  pageNumber?: number,
  extra?: Record<string, any>
): Promise<void> {
  // Only log actual errors, not successes
  if (eventType === 'load_success') {
    return;
  }

  try {
    const sb = getSupabase();
    const { data: { user } } = await sb.auth.getUser();

    // Don't store base64 data URIs - just log that it was a data URI
    const isDataUri = imageUrl.startsWith('data:');
    const urlToLog = isDataUri ? `[data-uri:${imageUrl.length} bytes]` : imageUrl;

    await sb.from(IMAGE_ERRORS_TABLE).insert({
      user_id: user?.id || null,
      book_id: bookId || null,
      page_number: pageNumber ?? null,
      image_url: urlToLog,
      error_type: eventType,
      user_agent: navigator.userAgent,
      extra: isDataUri ? { ...extra, isDataUri: true } : extra || null
    });
  } catch (e) {
    // Silent fail - don't break the app for logging
    console.error('Failed to log image event:', e);
  }
}

/**
 * Helper to convert base64 to a Blob for storage upload
 */
function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: contentType });
}

/**
 * Helper specifically for the raw PCM audio data which doesn't have a data URI header
 */
function pcmBase64ToBlob(base64: string): Blob {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'audio/pcm' });
}

export const storyService = {
  async getStories(userId: string, childId?: string): Promise<GeneratedBook[]> {
    try {
      debugLog("STORY", "storyService: Loading library", { userId, childId });
      let query = getSupabase()
        .from(TABLE_NAME)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (childId) {
        query = query.eq('child_id', childId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map(row => ({
        ...row.book_data,
        id: row.id,
        userId: row.user_id,
        createdAt: row.created_at
      }));
    } catch (e) {
      console.error("Supabase Library Load Failed:", e);
      return [];
    }
  },

  /**
   * Offloads base64 assets to Supabase Storage and returns the public URL
   */
  async uploadAsset(userId: string, folderId: string, fileName: string, data: string, isAudio = false): Promise<string> {
    if (!data || !data.startsWith('data:') && !isAudio) return data;
    if (data.startsWith('http')) return data; // Already a URL

    try {
      const blob = isAudio ? pcmBase64ToBlob(data) : base64ToBlob(data);
      const path = `${userId}/${folderId}/${fileName}`;
      const sb = getSupabase();
      
      const { error: uploadError } = await sb.storage
        .from(BUCKET_NAME)
        .upload(path, blob, { upsert: true, contentType: isAudio ? 'audio/pcm' : undefined });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = sb.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);

      return publicUrl;
    } catch (e) {
      console.error(`Asset upload failed for ${fileName}:`, e);
      // Log upload failure (don't await - fire and forget)
      if (!isAudio) {
        logImageEvent('upload_error', `${userId}/${folderId}/${fileName}`, folderId, undefined, {
          error: e instanceof Error ? e.message : String(e),
          dataLength: data?.length || 0
        });
      }
      return isAudio ? '' : data; // Avoid persisting raw PCM when upload fails
    }
  },

  async saveStory(userId: string, book: GeneratedBook, isPro: boolean): Promise<{ success: boolean; message: string; savedBook?: GeneratedBook }> {
    debugLog("STORY", "saveStory: Initiated", { userId, isPro, title: book.title });
    
    try {
      const sb = getSupabase();
      // 1. Limit Check
      if (!book.id) {
        const { count, error: countError } = await sb
          .from(TABLE_NAME)
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);

        if (countError) throw new Error("Could not verify library capacity: " + countError.message);

        const limit = isPro ? LIMIT_PRO : LIMIT_FREE;
        if ((count || 0) >= limit) {
          return { 
            success: false, 
            message: isPro 
              ? `Library full (${LIMIT_PRO} stories). Please remove an old story.` 
              : `Free limit reached (${LIMIT_FREE} stories). Upgrade to Pro!` 
          };
        }
      }

      // 2. Asset Offloading (Move heavy base64 strings to Storage)
      const folderId = book.id || crypto.randomUUID();
      
      debugLog("STORY", "saveStory: Offloading assets to Storage...");
      
      const coverUrl = await this.uploadAsset(userId, folderId, 'cover.jpg', book.coverImageUrl || '');
      
      const processedPages: BookPage[] = await Promise.all(book.pages.map(async (p, idx) => {
        const imageUrl = await this.uploadAsset(userId, folderId, `page_${idx}.jpg`, p.imageUrl || '');
        const audioUrl = await this.uploadAsset(userId, folderId, `audio_${idx}.pcm`, p.audioBase64 || '', true);
        return {
          ...p,
          imageUrl,
          audioBase64: audioUrl // Reusing the same field name to avoid breaking the reader types
        };
      }));

      // 3. Data Sanitization & Payload Prep (Lightweight JSON)
      const storyData = {
        title: book.title,
        synopsis: book.synopsis,
        authorName: book.authorName,
        voiceName: book.voiceName,
        characterBible: book.characterBible,
        coverImagePrompt: book.coverImagePrompt,
        coverImageUrl: coverUrl,
        pages: processedPages,
        estimatedReadingTime: book.estimatedReadingTime,
        tags: book.tags,
        illustrationStyle: book.illustrationStyle
      };

      // 4. Execution (Upsert)
      const rowToUpsert: any = {
        user_id: userId,
        title: book.title,
        synopsis: book.synopsis,
        book_data: storyData
      };

      if (book.id) {
        rowToUpsert.id = book.id;
      }

      const { data: upsertData, error: upsertError } = await sb
        .from(TABLE_NAME)
        .upsert(rowToUpsert, { onConflict: 'id' })
        .select()
        .single();

      if (upsertError) throw new Error(upsertError.message);

      const savedBook: GeneratedBook = {
        ...upsertData.book_data,
        id: upsertData.id,
        userId: upsertData.user_id,
        createdAt: upsertData.created_at
      };

      debugLog("STORY", "saveStory: Success", { id: upsertData.id });
      return { success: true, message: "Story saved successfully!", savedBook };

    } catch (e: any) {
      console.error("Supabase Story Save Failed:", e);
      return { 
        success: false, 
        message: `Save Error: ${e.message || "Unknown Connection Issue"}`
      };
    }
  },

  async deleteStory(userId: string, storyId: string): Promise<GeneratedBook[]> {
    try {
      debugLog("STORY", "deleteStory: Started", { storyId });
      
      const { error } = await getSupabase()
        .from(TABLE_NAME)
        .delete()
        .eq('id', storyId)
        .eq('user_id', userId);

      if (error) throw error;
      return this.getStories(userId);
    } catch (e) {
      console.error("Supabase Story Delete Failed:", e);
      return [];
    }
  }
};
